import type { SyncDiagnosticException } from './syncDiagnosticException';
import { SupportDiagnosticBuffer } from './SupportDiagnosticBuffer';
import type { SupportDiagnosticEventInput, SupportDiagnosticSnapshot } from '@/types/supportDiagnostic';

export interface SyncDiagnosticIdentity {
  companyId: string;
  userId: string;
  deviceId: string;
}

export interface SyncDiagnosticOutcome {
  traceId: string;
  startedAt: number;
  success: boolean;
  networkConnected: boolean;
  failure: 'local' | 'network' | 'other';
  errorCode: string;
  stage: string;
  exception?: SyncDiagnosticException;
}

interface Storage {
  getString(key: string): string | undefined;
  set(key: string, value: string): void;
  delete(key: string): void;
}

export const SYNC_DIAGNOSTIC_TTL_MS = 7 * 86400000;
const NETWORK_WINDOW_MS = 10 * 60000;

/** Uma captura imutável por identidade; não depende da disponibilidade do SQLite. */
export class SyncFailureJournal {
  constructor(private readonly storage: Storage, private readonly now = Date.now) {}

  private key(identity: SyncDiagnosticIdentity): string {
    return `sync_failure_v1:${identity.companyId}:${identity.userId}:${identity.deviceId}`;
  }

  read(identity: SyncDiagnosticIdentity): SupportDiagnosticSnapshot | null {
    const key = this.key(identity);
    try {
      const raw = this.storage.getString(key);
      if (!raw) return null;
      const snapshot = JSON.parse(raw) as SupportDiagnosticSnapshot;
      const meta = snapshot.metadata as Record<string, unknown>;
      const created = Date.parse(snapshot.createdAt);
      if (!Number.isFinite(created) || created > this.now() || this.now() - created >= SYNC_DIAGNOSTIC_TTL_MS ||
          !Array.isArray(snapshot.events) || !snapshot.diagnosticId ||
          meta.companyId !== identity.companyId || meta.userId !== identity.userId || meta.deviceId !== identity.deviceId) {
        this.storage.delete(key);
        return null;
      }
      return snapshot;
    } catch { return null; }
  }

  record(identity: SyncDiagnosticIdentity, outcome: SyncDiagnosticOutcome,
    events: readonly SupportDiagnosticEventInput[], createId: () => string,
    metadata: { platform: string; clientBuild: number }): boolean {
    try {
      if (!identity.companyId || !identity.userId || !identity.deviceId) return false;
      const key = this.key(identity);
      const networkKey = `${key}:network`;
      if (outcome.success) {
        this.storage.delete(networkKey);
        return false;
      }
      if (!outcome.networkConnected && outcome.failure === 'network') return false;
      if (outcome.failure === 'network') {
        let previous: { at: number; traceId: string }[] = [];
        try { previous = JSON.parse(this.storage.getString(networkKey) ?? '[]'); } catch { /* Reiniciar contador inválido. */ }
        if (!Array.isArray(previous)) previous = [];
        const recent = previous.filter(item => item.at <= this.now() && this.now() - item.at <= NETWORK_WINDOW_MS);
        if (!recent.some(item => item.traceId === outcome.traceId)) recent.push({ at: this.now(), traceId: outcome.traceId });
        this.storage.set(networkKey, JSON.stringify(recent.slice(-3)));
        if (recent.length < 3) return false;
      } else {
        this.storage.delete(networkKey);
      }
      if (this.read(identity)) return false;
      const lastSent = Number(this.storage.getString(`${key}:sent`) ?? 0);
      if (lastSent > 0 && this.now() - lastSent < 86400000) return false;
      const buffer = new SupportDiagnosticBuffer({ maxBytes: 512 * 1024 });
      events.forEach(event => buffer.record(event));
      buffer.record({ level: 'error', category: 'sync', name: 'sync_failed',
        data: { traceId: outcome.traceId, stage: outcome.stage, errorCode: outcome.errorCode, exception: outcome.exception ?? null } });
      const snapshot = buffer.createImmutableSnapshot({ diagnosticId: createId(),
        createdAt: new Date(this.now()).toISOString(), metadata: { ...identity, ...metadata,
          category: 'sync', traceId: outcome.traceId, stage: outcome.stage, errorCode: outcome.errorCode, exception: outcome.exception ?? null } });
      this.storage.set(key, JSON.stringify(snapshot));
      return true;
    } catch { return false; }
  }

  finish(identity: SyncDiagnosticIdentity, outcome: SyncDiagnosticOutcome): void {
    try {
      const snapshot = this.read(identity);
      if (!snapshot || (snapshot.metadata as any).traceId !== outcome.traceId) return;
      const buffer = new SupportDiagnosticBuffer({ maxBytes: 512 * 1024, previousStats: snapshot.stats });
      snapshot.events.forEach(event => buffer.record(event));
      buffer.record({ level: outcome.success ? 'info' : 'error', category: 'sync', name: 'sync_finished',
        data: { traceId: outcome.traceId, success: outcome.success, stage: outcome.stage } });
      this.storage.set(this.key(identity), JSON.stringify(buffer.createImmutableSnapshot({
        diagnosticId: snapshot.diagnosticId, createdAt: snapshot.createdAt, metadata: snapshot.metadata,
      })));
    } catch { /* O resumo não pode interferir no envio nem no sync. */ }
  }

  acknowledge(identity: SyncDiagnosticIdentity, diagnosticId: string): void {
    const key = this.key(identity);
    if (this.read(identity)?.diagnosticId !== diagnosticId) return;
    this.storage.set(`${key}:sent`, String(this.now()));
    this.storage.delete(key);
    this.storage.delete(`${key}:network`);
  }
}
